import axios from 'axios';
import config from '@/data/config';

/** globals */

const REFRESH_TOKEN_THRESHOLD = 1;
const REFRESH_DEBOUNCE_DELAY = 500;
let refreshTokenCounter = 0;
let tokenRenewalPromise = null;

/** http creation */

const httpV1 = axios.create({
  baseURL: `${config.BASE_API_URL}/v1`,
  headers: {
    'content-type': 'application/json',
  },
});
const httpV2 = axios.create({
  baseURL: `${config.BASE_API_URL}/v2`,
  headers: {
    'content-type': 'application/json',
  },
});

/** methods */

const getToken = () => {
  try {
    let store = localStorage.getItem('vuex');

    if (!store) throw new Error('Store not found');
    store = JSON.parse(store);

    if (!store.app.token) throw new Error('Token not found');
    return store.app.token;
  } catch (err) {
    return err;
  }
};

const renewToken = async () => {
  if (!tokenRenewalPromise) {
    tokenRenewalPromise = new Promise((resolve, reject) => {
      setTimeout(async () => {
        // prevent multiple requests
        if (sessionStorage.getItem('attempt')) {
          return new Promise((resolve) => {
            setTimeout(() => {
              try {
                const token = getToken();
                return resolve(token);
              } catch (err) {
                return reject(err);
              }
            }, 1500);
          });
        }

        // prevent infinite loop
        if (refreshTokenCounter > REFRESH_TOKEN_THRESHOLD) {
          return reject(new Error('Max attempts reached'));
        }

        // actual refresh token
        try {
          const response = await httpV1.post('/users/refresh-token');

          refreshTokenCounter += 1;
          sessionStorage.setItem('attempt', refreshTokenCounter);

          return resolve(response.data.data);
        } catch (err) {
          return reject(err);
        } finally {
          tokenRenewalPromise = null;
        }
      }, REFRESH_DEBOUNCE_DELAY);
    });
  }

  return tokenRenewalPromise;
};

const dispatchRefreshTokenEvent = (token) => {
  try {
    const tokenEvent = new CustomEvent('refreshToken', { detail: token });
    window.dispatchEvent(tokenEvent);
    sessionStorage.removeItem('attempt');
  } catch (err) {
    // Silent error
    console.log('Error dispatching token event:', err);
  }
};

/** interceptors :: methods */

const handleRequest = (config) => {
  try {
    const token = getToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;

    return config;
  } catch (err) {
    return Promise.reject(err);
  }
};

const handleResponseSuccess = (response) => response;

const handleResponseError = async (err) => {
  if (err?.response?.status !== 401) return Promise.reject(err);

  try {
    const { token } = await renewToken();

    dispatchRefreshTokenEvent(token);

    err.config.headers.Authorization = `Bearer ${token}`;

    return axios.request(err.config);
  } catch (err) {
    console.log(
      '%c handle-response-error ',
      'background:red;color:white;',
      err,
    );

    window.location.replace('/');

    return Promise.reject(err);
  }
};

/** instances */

httpV1.interceptors.request.use(handleRequest);
httpV2.interceptors.request.use(handleRequest);

httpV1.interceptors.response.use(handleResponseSuccess, handleResponseError);
httpV2.interceptors.response.use(handleResponseSuccess, handleResponseError);

export {
  httpV1,
  httpV2,
};
